import type { ReactNode } from 'react';
import BkPage from '../../Components/ui/BkPage';
import BkPanel from '../../Components/ui/BkPanel';
import type { TermsBlock, TermsSection } from '../../config/terms';

function Block({ block }: { block: TermsBlock }) {
  if (block.type === 'p') {
    return <p className="text-sm leading-relaxed text-bk-text/90">{block.text}</p>;
  }

  const ListTag = block.type === 'ol' ? 'ol' : 'ul';
  const listClass =
    block.type === 'ol'
      ? 'list-decimal space-y-2 pl-5 text-sm leading-relaxed text-bk-text/90'
      : 'list-disc space-y-2 pl-5 text-sm leading-relaxed text-bk-text/90';

  return (
    <ListTag className={listClass}>
      {block.items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ListTag>
  );
}

export type LegalTocEntry = { id: string; title: string };

export type LegalDocumentProps = {
  title: string;
  effectiveDate: string;
  intro: readonly string[];
  sections: readonly TermsSection[];
  /** Extra TOC anchors that render outside `sections` (e.g. attributions). */
  tocExtras?: readonly LegalTocEntry[];
  /** Extra content after the numbered sections (e.g. attributions). */
  after?: ReactNode;
  footer?: ReactNode;
};

export default function LegalDocument({
  title,
  effectiveDate,
  intro,
  sections,
  tocExtras = [],
  after,
  footer,
}: LegalDocumentProps) {
  const toc: LegalTocEntry[] = [
    ...sections.map(({ id, title: sectionTitle }) => ({
      id,
      title: sectionTitle,
    })),
    ...tocExtras,
  ];

  return (
    <BkPage narrow>
      <div className="mb-8 space-y-3">
        <h1 className="text-2xl font-bold uppercase tracking-wider text-bk-gold md:text-3xl">
          {title}
        </h1>
        <p className="text-xs uppercase tracking-wide text-bk-muted">
          Effective date: {effectiveDate}
        </p>
        <div className="space-y-3">
          {intro.map((paragraph) => (
            <p key={paragraph.slice(0, 40)} className="text-sm text-bk-muted md:text-base">
              {paragraph}
            </p>
          ))}
        </div>
      </div>

      <nav className="mb-10" aria-label={`${title} contents`}>
        <BkPanel>
          <p className="mb-2 text-xs uppercase tracking-wide text-bk-muted">Contents</p>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {toc.map((entry) => (
              <li key={entry.id}>
                <a
                  className="text-bk-gold underline-offset-2 hover:underline"
                  href={`#${entry.id}`}
                >
                  {entry.title}
                </a>
              </li>
            ))}
          </ol>
        </BkPanel>
      </nav>

      {sections.map((section) => (
        <section
          key={section.id}
          id={section.id}
          className="mb-8 scroll-mt-24 space-y-3"
          aria-labelledby={`legal-${section.id}`}
        >
          <h2
            id={`legal-${section.id}`}
            className="border-b border-bk-gold-dim/40 pb-2 text-lg font-bold uppercase tracking-wider text-bk-text"
          >
            {section.title}
          </h2>
          <div className="space-y-3">
            {section.blocks.map((block, index) => (
              <Block key={`${section.id}-${index}`} block={block} />
            ))}
          </div>
        </section>
      ))}

      {after}

      {footer && <div className="mt-10 text-sm text-bk-muted">{footer}</div>}
    </BkPage>
  );
}
